import { z } from 'zod';
import { nipSchema } from '../nip';
import { idSchema, versionSchema } from './common';

/**
 * Dane firmy z rejestrów publicznych po NIP (prośba Sebastiana 6.10.2026: „dodając klienta można wpisać NIP i dane
 * jego firmy się zaczytają same”). Rejestry: Biała lista VAT Ministerstwa Finansów (bez klucza) i REGON GUS
 * (BIR1.1, z kluczem `GUS_API_KEY`), adaptery w `@fos/integrations/company-registry`. Wywołania tylko z serwera,
 * wynik w pamięci podręcznej `company_registry_cache` (per workspace, RLS). Opis: docs/INTEGRATIONS.md
 * („Rejestry firm: NIP → dane firmy”).
 */

// ── Enumy ───────────────────────────────────────────────────────────────────
/** Status VAT (Biała lista): czynny, zwolniony, niezarejestrowany; REGON nie zna statusu VAT = nieznany. */
export const COMPANY_VAT_STATUSES = ['active', 'exempt', 'unregistered', 'unknown'] as const;
export type CompanyVatStatus = (typeof COMPANY_VAT_STATUSES)[number];

/** Źródło danych: Biała lista VAT (MF), REGON (GUS), atrapa (testy, praca bez sieci). */
export const COMPANY_REGISTRY_SOURCES = ['mf_whitelist', 'gus_regon', 'fake'] as const;
export type CompanyRegistrySource = (typeof COMPANY_REGISTRY_SOURCES)[number];

/** Pola, które „Odśwież dane z rejestru” może przepisać do karty klienta (nazwa wyświetlana zostaje osobno). */
export const COMPANY_REGISTRY_FIELDS = ['legalName', 'regon', 'krs', 'vatStatus', 'address'] as const;
export type CompanyRegistryField = (typeof COMPANY_REGISTRY_FIELDS)[number];

/**
 * Limity (DECISIONS 6.10.2026 „Dane firmy po NIP”): Biała lista ma 100 zapytań „search” dziennie na adres IP serwera
 * (po przekroczeniu blokada do północy — także wyszukiwarki na podatki.gov.pl z tego adresu), więc pytamy rzadko.
 */
export const COMPANY_REGISTRY_LIMITS = {
  /** zapytania do rejestru na osobę na godzinę (trafienia w pamięć podręczną się nie liczą) */
  perUserPerHour: 20,
  /** zapytania do rejestru na workspace na dobę (Europe/Warsaw) */
  perWorkspacePerDay: 60,
  /** ważność znalezionych danych w pamięci podręcznej */
  cacheTtlFoundSec: 7 * 24 * 3600,
  /** ważność wyniku „nie znaleziono” (nowa firma może się pojawić w rejestrze) */
  cacheTtlNotFoundSec: 24 * 3600,
  /** czas odpowiedzi jednego rejestru */
  timeoutMs: 8000,
} as const;

// ── Adres ───────────────────────────────────────────────────────────────────
const addressText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, { message: `Najwyżej ${max} znaków` });

/** Adres ustrukturyzowany (siedziba): pola tekstowe, puste = brak. Kraj: kod ISO („PL”) albo nazwa. */
export const companyAddressSchema = z.object({
  street: addressText(200).default(''),
  buildingNo: addressText(20).default(''),
  apartmentNo: addressText(20).default(''),
  postalCode: addressText(20).default(''),
  city: addressText(100).default(''),
  country: addressText(60).default('PL'),
});
export type CompanyAddress = z.output<typeof companyAddressSchema>;

export const EMPTY_COMPANY_ADDRESS: CompanyAddress = Object.freeze({
  street: '',
  buildingNo: '',
  apartmentNo: '',
  postalCode: '',
  city: '',
  country: 'PL',
}) as CompanyAddress;

/** Adres bez treści (same puste pola; kraj się nie liczy). */
export function isEmptyAddress(a: Partial<CompanyAddress> | null | undefined): boolean {
  if (!a) return true;
  return !(a.street || a.buildingNo || a.apartmentNo || a.postalCode || a.city);
}

export function isPolishCountry(country: string | null | undefined): boolean {
  return !country || /^(pl|polska|poland|rzeczpospolita polska)$/i.test(country.trim());
}

const POSTAL_AT_END = /(?:^|,\s*|\s)(\d{2}-\d{3})\s+([^,]+?)\s*$/;
// numer budynku (z literą, zakresem „12-14”), potem opcjonalnie lokal: „/5”, „lok. 5”, „m. 5”
const BUILDING_AT_END =
  /^(.*?\S)\s+(\d+[A-Za-z]?(?:-\d+[A-Za-z]?)?)(?:(?:\s*\/\s*|\s+(?:lok(?:al)?\.?|m\.?|apt\.?)\s*)([0-9]+[A-Za-z]?))?$/i;

/**
 * Adres jednym wierszem → pola (Biała lista podaje adres jako tekst: „ŚWIĘTOKRZYSKA 12, 00-916 WARSZAWA”, „PUŁAWSKA
 * 182 LOK. 5, 02-670 WARSZAWA”, „MARSZAŁKOWSKA 10/16, 00-590 WARSZAWA”). Ostrożnie: kod pocztowy i miejscowość tylko
 * z końca po „NN-NNN”, numer budynku tylko z końca części ulicy; czego nie da się rozpoznać, zostaje w polu „Ulica”
 * (nic nie ginie). „10/16” = budynek 10, lokal 16. Starsze dane klienta (adres jako tekst) czyta tak samo.
 */
export function parseAddressLine(line: string, country = 'PL'): CompanyAddress {
  const text = line.replace(/\s+/g, ' ').trim();
  const out: CompanyAddress = { ...EMPTY_COMPANY_ADDRESS, country };
  if (!text) return out;
  let rest = text;
  const postal = POSTAL_AT_END.exec(text);
  if (postal?.[1] && postal[2]) {
    out.postalCode = postal[1];
    out.city = postal[2].trim();
    rest = text
      .slice(0, postal.index)
      .replace(/[,\s]+$/, '')
      .trim();
  }
  if (!rest) return out;
  const segments = rest.split(/\s*,\s*/).filter(Boolean);
  const last = segments.pop() ?? '';
  const prefix = segments.join(', ');
  let street = last;
  if (/^\d+[A-Za-z]?(?:\s*\/\s*\d+[A-Za-z]?)?$/.test(last)) {
    // sam numer („15, 05-220 ZIELONKA”) — miejscowość bez ulic
    const [b = '', a = ''] = last.split(/\s*\/\s*/);
    out.buildingNo = b;
    out.apartmentNo = a;
    street = '';
  } else {
    const m = BUILDING_AT_END.exec(last);
    if (m?.[1] && m[2]) {
      street = m[1].trim();
      out.buildingNo = m[2];
      out.apartmentNo = m[3] ?? '';
    }
  }
  out.street = [prefix, street].filter(Boolean).join(', ');
  return out;
}

/** Pierwsza linia adresu: „ul. Prosta 12/4” (ulica, numer, lokal). */
export function addressStreetLine(a: Partial<CompanyAddress>): string {
  const number = [a.buildingNo, a.apartmentNo].filter(Boolean).join('/');
  return [a.street, number].filter(Boolean).join(' ');
}

/** Adres jednym wierszem: „ul. Prosta 12/4, 00-838 Warszawa” (+ kraj, gdy nie Polska). */
export function formatCompanyAddress(a: Partial<CompanyAddress> | null | undefined): string {
  if (!a) return '';
  const line2 = [a.postalCode, a.city].filter(Boolean).join(' ');
  const country = a.country && !isPolishCountry(a.country) ? a.country : '';
  return [addressStreetLine(a), line2, country].filter(Boolean).join(', ');
}

/** Wejście adresu: obiekt albo (starsze dane, MCP) adres jednym wierszem. Pusty adres = brak. */
export const companyAddressInputSchema = z
  .union([z.string().trim().max(500, { message: 'Adres jest za długi' }), companyAddressSchema])
  .transform((v): CompanyAddress | undefined => {
    const a = typeof v === 'string' ? parseAddressLine(v) : v;
    return isEmptyAddress(a) ? undefined : a;
  });

// ── Nazwa wyświetlana ───────────────────────────────────────────────────────
const LEGAL_FORM_SUFFIXES = [
  'spółka z ograniczoną odpowiedzialnością',
  'sp\\. ?z ?o\\. ?o\\.',
  'sp\\. ?z ?o\\.o\\.',
  'spółka akcyjna',
  'prosta spółka akcyjna',
  's\\.a\\.',
  'p\\.s\\.a\\.',
  'spółka komandytowo-akcyjna',
  's\\.k\\.a\\.',
  'spółka komandytowa',
  'sp\\. ?k\\.',
  'spółka jawna',
  'sp\\. ?j\\.',
  'spółka partnerska',
  'sp\\. ?p\\.',
  'spółka cywilna',
  's\\.c\\.',
];
const SUFFIX_RE = new RegExp(`[\\s,]+(?:${LEGAL_FORM_SUFFIXES.join('|')})\\s*$`, 'iu');
// trzyliterowe imiona (nie skróty): „JAN KOWALSKI” → „Jan Kowalski”
const SHORT_NAMES = new Set([
  'jan',
  'ewa',
  'iga',
  'ada',
  'ola',
  'ala',
  'ula',
  'iza',
  'eda',
  'leo',
  'max',
  'tom',
  'ida',
]);
const LOWER_WORDS = new Set([
  'i',
  'w',
  'z',
  'o',
  'a',
  'u',
  'na',
  'do',
  'od',
  'po',
  'za',
  'dla',
  'pod',
  'nad',
  'we',
  'ze',
  'oraz',
]);

function titleWord(word: string, first: boolean): string {
  return word
    .split('-')
    .map((part, i) => {
      const letters = part.replace(/[^\p{L}]/gu, '');
      if (!letters || /\d/.test(part)) return part;
      const lower = part.toLocaleLowerCase('pl-PL');
      if (!first && i === 0 && LOWER_WORDS.has(lower)) return lower;
      // skróty (REA, GUS, PKO) zostają wielkimi literami
      if (letters.length <= 3 && !SHORT_NAMES.has(lower)) return part;
      const idx = lower.search(/\p{L}/u);
      return lower.slice(0, idx) + lower.charAt(idx).toLocaleUpperCase('pl-PL') + lower.slice(idx + 1);
    })
    .join('-');
}

/**
 * Propozycja nazwy wyświetlanej z nazwy prawnej: bez formy prawnej i bez krzyku wielkich liter
 * („REA NIERUCHOMOŚCI SP. Z O.O.” → „REA Nieruchomości”). Nazwa z małymi literami zostaje (tylko bez formy prawnej).
 * To tylko podpowiedź — pole „Nazwa” jest zawsze edytowalne.
 */
export function suggestDisplayName(legalName: string): string {
  let name = legalName.replace(/\s+/g, ' ').trim();
  for (let i = 0; i < 4; i++) {
    const next = name.replace(SUFFIX_RE, '').trim();
    if (next === name || !next) break;
    name = next;
  }
  name = name.replace(/[\s,]+$/, '');
  const hasLower = /\p{Ll}/u.test(name);
  if (hasLower) return name;
  return name
    .split(' ')
    .map((w, i) => titleWord(w, i === 0))
    .join(' ');
}

// ── Wynik rejestru ──────────────────────────────────────────────────────────
export const companyRegistryDataSchema = z.object({
  legalName: z.string().min(1).max(500),
  shortName: z.string().max(300).optional(),
  nip: z.string().regex(/^\d{10}$/),
  regon: z.string().max(14).optional(),
  krs: z.string().max(10).optional(),
  vatStatus: z.enum(COMPANY_VAT_STATUSES),
  address: companyAddressSchema,
  workingAddress: companyAddressSchema.optional(),
  /** rachunki rozliczeniowe — tylko z Białej listy (dane publiczne); nie trafiają do karty klienta */
  bankAccounts: z.array(z.string().max(34)).max(50).optional(),
  /** data zakończenia działalności (REGON), YYYY-MM-DD */
  closedOn: z.string().max(10).optional(),
});
export type CompanyRegistryData = z.output<typeof companyRegistryDataSchema>;

/** Wynik zapytania adaptera (`CompanyRegistry.lookupByNip`): znaleziono albo nie (błąd = INTEGRATION_UNAVAILABLE). */
export const companyRegistryResultSchema = z.discriminatedUnion('found', [
  companyRegistryDataSchema.extend({
    found: z.literal(true),
    source: z.enum(COMPANY_REGISTRY_SOURCES),
    fetchedAt: z.coerce.date(),
    raw: z.unknown().optional(),
  }),
  z.object({
    found: z.literal(false),
    source: z.enum(COMPANY_REGISTRY_SOURCES),
    fetchedAt: z.coerce.date(),
    nip: z.string().regex(/^\d{10}$/),
  }),
]);
export type CompanyRegistryResult = z.output<typeof companyRegistryResultSchema>;

/**
 * Port rejestru firm (implementacje w `@fos/integrations/company-registry`: MF, GUS, atrapa; @fos/core nie zależy
 * od @fos/integrations — API wstrzykuje adapter przez `configureCompanyRegistry`). Błędy: AppError
 * `INTEGRATION_UNAVAILABLE` z komunikatem PL (bez treści odpowiedzi dostawcy).
 */
export interface CompanyRegistry {
  readonly id: string;
  readonly simulated: boolean;
  lookupByNip(nip: string, opts?: { signal?: AbortSignal }): Promise<CompanyRegistryResult>;
}

// ── Usługi (moduł crm) ──────────────────────────────────────────────────────
/** `clients.lookupNip` (crm.lookupCompanyByNip): dane firmy po NIP bez zapisu w karcie klienta. */
export const lookupCompanyByNipInput = z.object({ nip: nipSchema }).strict();
export type LookupCompanyByNipInput = z.input<typeof lookupCompanyByNipInput>;

/** Klient z tym samym NIP (widoczny dla osoby) — ostrzeżenie „Ten klient już jest” z linkiem. */
export interface ClientNipDuplicate {
  id: string;
  name: string;
  archived: boolean;
}

export interface CompanyLookupOutput {
  nip: string;
  found: boolean;
  source: CompanyRegistrySource;
  /** kiedy dane pobrano z rejestru (z pamięci podręcznej: data pierwszego pobrania) */
  fetchedAt: Date;
  /** wynik z pamięci podręcznej (bez zapytania do rejestru) */
  cached: boolean;
  /** atrapa rejestru (praca bez sieci) — UI pokazuje „Symulacja” */
  simulated: boolean;
  company: CompanyRegistryData | null;
  /** propozycja nazwy wyświetlanej („REA Nieruchomości”) */
  suggestedName: string | null;
  duplicates: ClientNipDuplicate[];
}

/** `clients.findByNip` (crm.findClientsByNip): klienci z tym NIP w zakresie osoby (bez zapytania do rejestru). */
export const findClientsByNipInput = z.object({ nip: nipSchema, excludeClientId: idSchema.optional() }).strict();
export type FindClientsByNipInput = z.input<typeof findClientsByNipInput>;

export interface FindClientsByNipOutput {
  nip: string;
  duplicates: ClientNipDuplicate[];
}

/**
 * `clients.applyRegistry` (crm.applyRegistryData): zapis wybranych pól z danych rejestru (z pamięci podręcznej, dla NIP
 * klienta — wartości nie przychodzą od przeglądarki). `version` z karty klienta (inaczej CONFLICT).
 */
export const applyRegistryDataInput = z
  .object({
    clientId: idSchema,
    version: versionSchema,
    fields: z
      .array(z.enum(COMPANY_REGISTRY_FIELDS))
      .min(1, { message: 'Wybierz co najmniej jedno pole' })
      .max(COMPANY_REGISTRY_FIELDS.length),
  })
  .strict();
export type ApplyRegistryDataInput = z.input<typeof applyRegistryDataInput>;

export const COMPANY_REGISTRY_MESSAGES = {
  notConfigured: 'Pobieranie danych z rejestru jest wyłączone. Wpisz dane firmy ręcznie.',
  unavailable: 'Rejestr firm nie odpowiada. Wpisz dane ręcznie albo spróbuj później.',
  userLimit: 'Za dużo zapytań do rejestru w ostatniej godzinie. Wpisz dane ręcznie albo spróbuj później.',
  workspaceLimit:
    'Dzienny limit zapytań do rejestru w tym workspace jest wyczerpany. Wpisz dane ręcznie albo spróbuj jutro.',
  noNip: 'Klient nie ma polskiego NIP. Wpisz NIP w danych klienta.',
  stale: 'Dane z rejestru wygasły. Pobierz je ponownie.',
  notFound: 'Nie znaleźliśmy firmy z tym NIP w rejestrze.',
} as const;
